// @flow

import EventEmitter from "events";

import type { InnovationFrame } from "@/components/InnovationAnimation/InnovationAnimation";

interface CanvasFactory {
    /**
     * Create a canvas with given width and height.
     * @param width
     * @param height
     */
    create(width: number, height: number): HTMLCanvasElement;
}

class CreateCanvasFactory implements CanvasFactory {
    +document: Document;

    constructor(document: Document) {
        this.document = document;
    }

    create(width: number, height: number): HTMLCanvasElement {
        if (window.OffscreenCanvas != null) {
            return new window.OffscreenCanvas(width, height);
        }
        const canvas = this.document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        return canvas;
    }
}

/**
 * Creates a HTMLImageElement and wait for the source to load.
 * @param source
 * @return {Promise<R>|Promise<unknown>}
 */
function loadImage(source: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () =>
            reject(new Error(`Failed to load image: ${source}`));
        image.src = source;
    });
}

export interface FrameAddedPayload<T> {
    frame: number;
    width: number;
    height: number;
    offsetY: number;
    context: T;
}

/**
 * Creates an offscreen 2D context and draws each image into it.
 */
class FramePreRenderer extends EventEmitter {
    /**
     * The width of each frame (Assumes all frame sizes are equal to the first one)
     * @type {number}
     */
    +frameWidth: number;

    /**
     * The height of each frame (Assumes all frame sizes are equal to the first one)
     * @type {number}
     */
    +frameHeight: number;

    /**
     * The sources of the images to pre-render.
     * @type {string[]}
     */
    +sources: string[];

    /**
     * The offscreen context.
     * @type {CanvasRenderingContext2D}
     */
    +context: CanvasRenderingContext2D;

    /**
     * @param image - First image in the sources list
     * @param sources - List of image sources
     * @param factory - Factory for creating a canvas.
     */
    constructor(
        image: HTMLImageElement,
        sources: string[],
        factory: CanvasFactory,
    ) {
        super();
        this.frameWidth = image.width;
        this.frameHeight = image.height;
        this.sources = sources;
        const canvas = factory.create(
            this.frameWidth,
            // We want to draw each image below each other, so make sure there is enough space.
            this.frameHeight * sources.length,
        );
        this.context = canvas.getContext("2d", {
            alpha: false,
            antialias: false,
        });
        this.context.imageSmoothingEnabled = false;
        this.context.drawImage(image, 0, 0, this.frameWidth, this.frameHeight);
    }

    /**
     * Loops through all sources and loads & pre-renders them onto the canvas.
     */
    preload() {
        // Skip the first source, this one has already been loaded when creating this instance.
        for (let index = 1; index < this.sources.length; index += 1) {
            this.preloadFrame(index);
        }
    }

    /**
     * Load the image and draw it onto the canvas.
     * @param frame
     */
    preloadFrame(frame: number) {
        const source = this.sources[frame];
        loadImage(source).then(image => {
            const payload: FrameAddedPayload<FramePreRenderer> = {
                frame: frame,
                height: this.frameHeight,
                width: this.frameWidth,
                // Start drawing this image below the last one.
                offsetY: this.frameHeight * frame,
                context: this,
            };
            this.context.drawImage(
                image,
                0,
                payload.offsetY,
                payload.width,
                payload.height,
            );
            this.emit("frame-added", payload);
        });
    }

    /**
     * Load the first image in the list and create a new instance of FramePreRenderer.
     * @param sources
     * @param factory
     * @return {Promise<FramePreRenderer>}
     */
    static create(sources: string[], factory: CanvasFactory): Promise<FramePreRenderer> {
        if (sources.length <= 0) {
            throw new Error("Empty source list.");
        }
        return loadImage(sources[0]).then(image => new FramePreRenderer(image, sources, factory));
    }
}

class Frame {
    image: ImageData | null;
    element: HTMLImageElement | null;
    +source: string;
    loading: boolean = false;

    constructor(source: string) {
        this.image = null;
        this.element = null;
        this.source = source;
    }

    get canRender(): boolean {
        return this.image != null || this.element != null;
    }

    load(): void | Promise<Frame> {
        if (this.loading) {
            return;
        }

        this.loading = true;
        return loadImage(this.source).then(image => {
            this.element = image;
            this.loading = false;
            return this;
        });
    }

    /**
     * Renders the frame to the context if image has been loaded.
     * @param context
     * @return {boolean} - Wether this frame did render or not
     */
    render(context: CanvasRenderingContext2D): boolean {
        const { image, element } = this;
        if (image != null) {
            context.putImageData(image, 0, 0);
            return true;
        }
        if (element == null) {
            return false;
        }
        context.drawImage(element, 0, 0);
        this.image = context.getImageData(0, 0, element.width, element.height);
        // Clear the image element to free some memory (IE11)
        this.element = null;
        return true;
    }

    static create(source: string, image: HTMLImageElement): Frame {
        const frame = new Frame(source);
        frame.element = image;
        frame.loading = false;
        return frame;
    }
}

class Page {
    +from: number;
    +to: number;
    +frames: Frame[];

    didPreload: boolean = false;

    constructor(from: number, to: number, frames: Frame[]) {
        this.from = from;
        this.to = to;
        this.frames = frames.slice(from, to);
    }

    preload(): Promise<any> {
        this.didPreload = true;
        return Promise.all(this.frames.map(frame => frame.load()));
    }

    static createPages(frames: Frame[], initial: number, perPage: number): Array<Page> {
        const pages = [new Page(0, initial, frames)];
        for (let i = initial; i <= frames.length; i += perPage) {
            pages.push(new Page(i, i + perPage, frames));
        }

        return pages;
    }

    static preload(pages: Page[], currentFrame: number) {
        const currentPage = pages.findIndex(
            page => currentFrame >= page.from && currentFrame <= page.to,
        );
        if (currentPage !== -1 && currentPage + 1 < pages.length) {
            const page = pages[currentPage + 1];
            if (!page.didPreload) {
                page.preload();
            }
        }
    }
}

/**
 * Would be nice to re-use the FrameRenderer class for the innovation animation, but a canvas has a limited size.
 */
class LargeFramePreRenderer extends EventEmitter {
    /**
     * The width of each frame (Assumes all frame sizes are equal to the first one)
     * @type {number}
     */
    +frameWidth: number;

    /**
     * The height of each frame (Assumes all frame sizes are equal to the first one)
     * @type {number}
     */
    +frameHeight: number;

    /**
     * The sources of the images to pre-render.
     * @type {string[]}
     */
    +sources: string[];

    +frames: Frame[];

    +pages: Page[];

    /**
     * @param image - First image in the sources list
     * @param sources - List of image sources
     */
    constructor(image: HTMLImageElement, sources: string[]) {
        super();
        this.frameWidth = image.width;
        this.frameHeight = image.height;
        this.sources = sources;
        this.frames = sources.map(source => new Frame(source));
        this.frames[0] = Frame.create(sources[0], image);
        this.pages = Page.createPages(this.frames, 40, 20);
        this.pages[0].preload();
    }

    renderFrame(index: number, context: CanvasRenderingContext2D): boolean {
        if (index >= this.frames.length) {
            return false;
        }

        Page.preload(this.pages, index);
        const frame = this.frames[index];

        if (frame.render(context)) {
            return true;
        }

        // Render the next loaded frame
        for (const frame of this.frames.slice(index)) {
            if (frame.canRender && frame.render(context)) {
                return true;
            }
        }

        return false;
    }

    static getSizeKey(): "large" | "sm" {
        const windowWidth = window.innerWidth;

        if (windowWidth <= 1200) {
            return "sm";
        }

        return "large";
    }

    static getQualityLevel(start: Date, end: Date): "original" | "compressed" {
        const diff = end.getTime() - start.getTime();

        if (diff > 300) {
            // TODO: Compressed images are smaller than the original
            return "original";
        }

        return "original";
    }

    /**
     * Load the first image in the list and create a new instance of LargeFramePreRenderer.
     * @param sources
     * @return {Promise<FramePreRenderer>}
     */
    static create(sources: InnovationFrame[]): Promise<LargeFramePreRenderer> {
        if (sources.length <= 0) {
            throw new Error("Empty source list.");
        }
        const sizeKey = this.getSizeKey();
        const mapped = sources.map(source => source[sizeKey]);
        const firstSource = mapped[0].original;
        const start = new Date();
        return loadImage(firstSource).then(image => {
            const qualityLevel = this.getQualityLevel(start, new Date());
            return new LargeFramePreRenderer(
                image,
                mapped.map(s => s[qualityLevel]),
            );
        });
    }
}

export { CreateCanvasFactory, LargeFramePreRenderer };

export default FramePreRenderer;
